import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  PaperClipOutlined,
  DeleteOutlined,
  PlusSquareOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  InputNumber,
  Table,
  Collapse as AntCollapse,
  Descriptions,
  Modal as AntModal,
  Spin,
  Flex,
  Upload,
  Popover,
  Timeline,
  Select,
  DatePicker,
  Empty,
  Drawer,
  Tooltip as AntTooltip,
  Radio,
  Space,
  message,
} from "antd";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Col,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Spinner,
} from "reactstrap";
import invoiceApis from "../../api/Invoice/invoice";
import { nanoid } from "nanoid";
import ReactQuill from "react-quill";
import { Option } from "antd/es/mentions";
import Swal from "sweetalert2";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import PdfGenerator from "./SellerToBuyerInvoicePDF";
import { formatCurrency, formatDecimal } from "../../utils/Utils";
import miscService from "../../api/misc/misc";
import CropperComponent from "../components/CropperComponent";
import SignatureModal from "../purchase-orders/SignatureModal";
import Dragger from "antd/es/upload/Dragger";
import ReturnApprovalDrawer from "./ReturnApprovalDrawer";
import sequenceApis from "../../api/master/sequence";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  returnsMode,
  selectedRowKeys,
  onReturnQuantityChange,
  ...restProps
}: any) => {
  const isReturnsCell = dataIndex === "returnsQuantity";
  const isRowSelected = selectedRowKeys?.includes(
    record?.key || record?.lineNo
  );
  const hasExistingReturn = Number(record?.quantityReturned || 0) > 0;
  const isEligibleForReturn =
    record?.isEligibleForReturn === 1 && !hasExistingReturn;
  const showInput =
    editing ||
    (isReturnsCell && returnsMode && isRowSelected && isEligibleForReturn);

  // Calculate return qty for returns cell: (quantityDispatched - quantityDelivered)
  // Use quantityInvoiced as fallback if quantityDispatched doesn't exist
  const getInitialReturnQty = () => {
    if (isReturnsCell && record) {
      const quantityDispatched = Number(record?.quantityDispatched || record?.quantityInvoiced || 0);
      const quantityDelivered = Number(record?.quantityDelivered || 0);
      const calculatedReturnQty = quantityDispatched - quantityDelivered;
      return calculatedReturnQty > 0 ? calculatedReturnQty.toFixed(2) : undefined;
    }
    return undefined;
  };

  return (
    <td {...restProps}>
      {showInput ? (
        <Form.Item
          name={isReturnsCell ? `${dataIndex}_${record.lineNo}` : dataIndex}
          style={{ margin: 0 }}
          preserve={true}
          validateTrigger={
            isReturnsCell ? ["onChange", "onBlur"] : ["onSubmit"]
          }
          rules={
            isReturnsCell
              ? [
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    const returnQty = Number(value);
                    const deliveredQty = Number(record.quantityDelivered) || 0;
                    // Use quantityInvoiced as fallback if quantityDispatched doesn't exist
                    const dispatchedQty = Number(record.quantityDispatched || record.quantityInvoiced || 0);

                    // Minimum value should be (dispatchedQty - deliveredQty)
                    const minReturnQty = dispatchedQty - deliveredQty;
                    if (returnQty < minReturnQty) {
                      return Promise.reject(
                        new Error(
                          `Minimum return qty is ${minReturnQty} ${record?.uom}`
                        )
                      );
                    }

                    // Maximum value should be quantityDispatched
                    if (returnQty > dispatchedQty) {
                      return Promise.reject(
                        new Error(
                          `Cannot exceed quantityDispatched (${dispatchedQty} ${record?.uom})`
                        )
                      );
                    }

                    if (returnQty <= 0) {
                      return Promise.reject(
                        new Error("Must be greater than 0")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]
              : [
                {
                  type: "number",
                  min: 1,
                  message: `${title} must > 0`,
                },
              ]
          }
          {...(isReturnsCell
            ? {
              initialValue: getInitialReturnQty(),
            }
            : {
              initialValue: Number(record[dataIndex]) || 0,
            })}
        >
          {isReturnsCell ? (
            <Input
              className="w-100"
              type="text"
              onKeyPress={(event) => {
                if (!/^[0-9.]+$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and one decimal point
                const regex = /^\d*\.?\d*$/;
                if (!regex.test(value)) {
                  e.preventDefault();
                  return;
                }
                // Trigger check for return quantities (with delay to allow form validation)
                if (onReturnQuantityChange) {
                  setTimeout(() => {
                    onReturnQuantityChange();
                  }, 150);
                }
              }}
            />
          ) : (
            <InputNumber
              className="w-100"
              min={0}
              controls={false}
              step={1}
              onKeyPress={(event) => {
                if (!/^[0-9.]+$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
          )}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};
const { Panel } = AntCollapse;
function InvoiceComponent({ invoiceNumber, previousPath, extra }: any) {
  const { data: currentUser }: any = useSelector(
    (state: RootState) => state.currentUser
  );
  const { data: accessibility }: any = useSelector(
    (state: any) => state.accessibility
  );
  const canCreateReturn = accessibility?.["Return"]?.add === 1;
  const navigate = useNavigate();
  const [showSubmitBtn, setShowSubmitBtn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState("1");
  const [OriginalData, setOriginalData] = useState<any>({});
  const [updatedLineItems, setUpdatedLineItems] = useState<any>([]);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [materialData, setMaterialData] = useState<any>({});
  const [editedData, setEditedData] = useState<any[]>([]);
  const [signature, setSignature] = useState<any>([]);
  const [DCAttachment, setDCAttachment] = useState<any>(null);
  const [newDCAttachment, setNewDCAttachment] = useState<any>(null);
  const [newInvoiceAttachment, setNewInvoiceAttachment] = useState<any>(null);
  const [InvoiceAttachment, setInvoiceAttachment] = useState<any>(null);
  const [termsVisible, setTermsVisible] = useState(false);
  const [bankDetailsVisible, setBankDetailsVisible] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState<any>("");
  const [newTermsAndConditions, setNewTermsAndConditions] = useState<any>("");
  const [dms, setDms] = useState<any>([]);
  const [isTermsAndConditionsChanged, setIsTermsAndConditionsChanged] =
    useState(false);
  const [dataSource, setDataSource] = useState<any>([]);
  const [currency, setCurrency] = useState<any>({
    currencyFormat: "",
    currencySymbol: "",
  });
  const [newInvoiceType, setNewInvoiceType] = useState<any>("");
  const [newInvoiceDate, setNewInvoiceDate] = useState<any>();
  const [newDueDate, setNewDueDate] = useState<any>();
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [returnsForm] = Form.useForm();
  const [additionalCharges, setAdditionalCharges] = useState<any>([]);
  const [editAdditionalCharges, setEditAdditionalCharges] =
    useState<any>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [invoiceDisapproveInvoice, setInvoiceDisapproveInvoice] =
    useState<any>(false);
  const [readOnly, setReadOnly] = useState<any>(true);
  const [returnsMode, setReturnsMode] = useState(false);
  const [returnsDrawerVisible, setReturnsDrawerVisible] = useState(false);
  const [returnableLines, setReturnableLines] = useState<any>([]);
  const [returnsRemarks, setReturnsRemarks] = useState("");
  const [returnsType, setReturnsType] = useState<string>("");
  const [returnsAttachments, setReturnsAttachments] = useState<any>([]);
  const [returnsSignature, setReturnsSignature] = useState<any>({
    isActive: false,
    label: "Authorised Signatory",
    image: null,
  });
  const location = useLocation();
  const [showReturnsCropModel, setShowReturnsCropModel] =
    useState<boolean>(false);
  const [showReturnsSignatureModel, setShowReturnsSignatureModel] =
    useState<boolean>(false);
  const [isReturnsFormValid, setIsReturnsFormValid] = useState(false);
  const [hasReturnQuantities, setHasReturnQuantities] = useState(false);
  const [hasInvalidReturnQty, setHasInvalidReturnQty] = useState(false);
  const [hasReturnSequence, setHasReturnSequence] = useState(false);
  const [activeReturnSequences, setActiveReturnSequences] = useState<any[]>([]);
  const [returnNumberingMode, setReturnNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showReturnNumberingModal, setShowReturnNumberingModal] = useState(false);
  const [tempReturnNumberingMode, setTempReturnNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [returnApprovalRequired, setReturnApprovalRequired] = useState("");
  const [returnsApprovals, setReturnsApprovals] = useState<any>({
    open: false,
    data: [],
  });
  const [createReturnLoading, setCreateReturnLoading] = useState(false);

  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  const computeReturnsFormValidity = useCallback(
    (formValues?: any) => {
      if (!returnsDrawerVisible) {
        return false;
      }

      const values =
        formValues && Object.keys(formValues).length > 0
          ? formValues
          : returnsForm.getFieldsValue();

      const hasReturnType = !!values.returnType;
      const hasApprovalRequired = !!values.approvalRequired;
      const hasReturnDate = !!values.returnDate;
      const hasReturnNumber =
        returnNumberingMode === 'auto' ||
        (values.returnNumber !== undefined &&
          values.returnNumber !== null &&
          values.returnNumber !== "");

      const approvalNeeded =
        (values.approvalRequired || "").toString().toLowerCase() === "yes";
      const hasApprovals =
        !approvalNeeded ||
        (Array.isArray(returnsApprovals?.data) &&
          returnsApprovals.data.length > 0);

      return (
        hasReturnType &&
        hasApprovalRequired &&
        hasReturnDate &&
        hasReturnNumber &&
        hasApprovals
      );
    },
    [
      returnNumberingMode,
      returnsApprovals?.data,
      returnsDrawerVisible,
      returnsForm,
    ]
  );

  useEffect(() => {
    if (returnsDrawerVisible) {
      setIsReturnsFormValid(computeReturnsFormValidity());
    }
  }, [
    returnsDrawerVisible,
    returnsApprovals?.data,
    hasReturnSequence,
    computeReturnsFormValidity,
  ]);

  const isEditing = (record: any) => record.key === editingKey;

  const edit = (record: any) => {
    form.setFieldsValue({ quantity: "", unitPrice: "", ...record });
    setEditingKey(record.key);
  };

  const save = async (key: React.Key) => {
    try {
      const row: any = await form.validateFields();
      const newData: any = [...dataSource];
      const index = newData.findIndex((item: any) => key == item.lineNo);
      if (index > -1) {
        setUpdatedLineItems((pre: any) => {
          if (!pre.includes(key)) {
            return pre.concat(key);
          }
          return pre;
        });
        const item: any = newData[index];
        const quantityInvoiced = row?.quantityInvoiced;
        const unitPrice = Number(row.unitPrice);
        const basicAmount = quantityInvoiced * unitPrice;
        const sgst = (basicAmount * Number(item.sgstPercentage)) / 100;
        const igst = (basicAmount * Number(item.igstPercentage)) / 100;
        const cgst = (basicAmount * Number(item.cgstPercentage)) / 100;
        const updatedRow = {
          ...item,
          quantityInvoiced,
          unitPrice,
          basicAmount,
          sgst,
          igst,
          cgst,
          grossTotal: basicAmount + sgst + igst + cgst,
        };
        console.log(index);
        console.log(updatedRow);

        newData.splice(index, 1, { ...updatedRow });
        setDataSource(newData);
        setEditingKey(null);
        setEditedData(
          newData.filter((item: any) => selectedRowKeys.includes(item.lineNo))
        );
      }
      setShowSubmitBtn(true);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const cancel = () => {
    setEditingKey(null);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys || newSelectedRowKeys.length === 0) {
      dataSource.forEach((item: any) => {
        form.setFieldsValue({ [`returnsQuantity_${item.lineNo}`]: undefined });
      });
      setHasReturnQuantities(false);
      setHasInvalidReturnQty(false);
      return;
    }
    // Set calculated return quantities for selected eligible rows
    newSelectedRowKeys.forEach((key) => {
      const item = dataSource.find((i: any) => i?.lineNo === key);
      if (item && item?.isEligibleForReturn === 1) {
        // Use quantityInvoiced as fallback if quantityDispatched doesn't exist
        const quantityDispatched = Number(item?.quantityDispatched || item?.quantityInvoiced || 0);
        const quantityDelivered = Number(item?.quantityDelivered || 0);
        const calculatedReturnQty = quantityDispatched - quantityDelivered;
        // Only set if not already set by user and calculated value is positive
        const currentValue = form.getFieldValue(`returnsQuantity_${item.lineNo}`);
        if (!currentValue && calculatedReturnQty > 0) {
          form.setFieldsValue({
            [`returnsQuantity_${item.lineNo}`]: calculatedReturnQty.toFixed(2),
          });
        }
      }
    });
    // Check if any selected row has return quantity entered (with delay to allow form validation)
    setTimeout(() => {
      checkReturnQuantities(newSelectedRowKeys);
    }, 150);
  };

  const checkReturnQuantities = (keys: React.Key[]) => {
    let hasValues = false;
    let hasInvalid = false;

    dataSource.forEach((item: any) => {
      if (keys.includes(item?.lineNo) && item?.isEligibleForReturn === 1) {
        const fieldName = `returnsQuantity_${item?.lineNo}`;
        const value = form.getFieldValue(fieldName);
        const fieldError = form.getFieldError(fieldName);

        if (value && Number(value) > 0) {
          hasValues = true;
          const returnQty = Number(value);
          const deliveredQty = Number(item?.quantityDelivered) || 0;
          // Use quantityInvoiced as fallback if quantityDispatched doesn't exist
          const dispatchedQty = Number(item?.quantityDispatched || item?.quantityInvoiced || 0);

          // Check form validation errors first
          if (fieldError && fieldError.length > 0) {
            hasInvalid = true;
          }

          // Also check business logic validation
          // Check if return quantity is less than minimum (dispatchedQty - deliveredQty)
          const minReturnQty = dispatchedQty - deliveredQty;
          if (returnQty < minReturnQty) {
            hasInvalid = true;
          }
          // Check if return quantity exceeds maximum (quantityDispatched)
          if (returnQty > dispatchedQty) {
            hasInvalid = true;
          }
          // Check if return quantity is less than or equal to 0
          if (returnQty <= 0) {
            hasInvalid = true;
          }
        }
      }
    });

    setHasReturnQuantities(hasValues);
    setHasInvalidReturnQty(hasInvalid);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled:
        record?.isEligibleForReturn !== 1 ||
        Number(record?.quantityReturned || 0) > 0,
    }),
  };

  const showReturnsDrawer = async () => {
    try {
      const rows = await form.validateFields();
      const returnsLines: any = [];

      // Collect return line items
      for (const item of dataSource) {
        if (
          selectedRowKeys.includes(item?.lineNo) &&
          item?.isEligibleForReturn === 1 &&
          rows[`returnsQuantity_${item?.lineNo}`] &&
          Number(rows[`returnsQuantity_${item?.lineNo}`]) > 0
        ) {
          const returnQty = Number(rows[`returnsQuantity_${item?.lineNo}`]);

          returnsLines.push({
            ...item,
            returnsQuantity: returnQty,
          });
        }
      }

      setReturnableLines(returnsLines);
      setReturnsDrawerVisible(true);
      setIsReturnsFormValid(false);
      setReturnsType("");
      setReturnApprovalRequired("");

      // Check if Return module has sequence configured and set form values
      await checkReturnSequence();

      // Set initial form values (returnNumber will be updated by useEffect based on mode)
      returnsForm.setFieldsValue({
        invoiceNumber: OriginalData?.invoiceId,
        returnNumber: "",
        returnDate: dayjs(),
        returnType: undefined,
        approvalRequired: undefined,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleDrawerClose = () => {
    setReturnsDrawerVisible(false);
    setCreateReturnLoading(false);
  };

  const closeReturnsDrawer = () => {
    setReturnsDrawerVisible(false);
    setReturnsMode(false);
    setSelectedRowKeys([]);
    setReturnsType("");
    setReturnsAttachments([]);
    setReturnsSignature({
      isActive: false,
      label: "Authorised Signatory",
      image: null,
    });
    setIsReturnsFormValid(false);
    setHasReturnQuantities(false);
    setHasInvalidReturnQty(false);
    setHasReturnSequence(false);
    setActiveReturnSequences([]);
    setReturnNumberingMode('auto');
    setReturnApprovalRequired("");
    setReturnsApprovals({
      open: false,
      data: [],
    });
    returnsForm.resetFields();
    setCreateReturnLoading(false);
  };

  const getLineReturnNumber = (lineItem: any) => {
    if (!lineItem) return undefined;
    if (lineItem.returnId) return lineItem.returnId;
    if (lineItem.returnNumber) return lineItem.returnNumber;
    if (
      Array.isArray(lineItem.returnNumbers) &&
      lineItem.returnNumbers.length
    ) {
      return lineItem.returnNumbers[0];
    }
    if (Array.isArray(lineItem.returnIds) && lineItem.returnIds.length) {
      return lineItem.returnIds[0];
    }
    return undefined;
  };

  const handleViewReturns = (lineReturnNumber?: string | number) => {
    if (lineReturnNumber) {
      navigate(`/view-return-details/${lineReturnNumber}`, {
        state: {
          previousPath: `${location.pathname}${location.search}`,
        },
      });
      return;
    }
    const invoiceIdentifier =
      OriginalData?.invoiceId || OriginalData?.invoiceNumber;
    if (invoiceIdentifier) {
      navigate(`/view-returns?invoiceId=${invoiceIdentifier}`, {
        state: {
          previousPath: `${location.pathname}${location.search}`,
        },
      });
    } else {
      navigate("/view-returns", {
        state: {
          previousPath: `${location.pathname}${location.search}`,
        },
      });
    }
  };

  const checkReturnSequence = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const returnSequence = data?.filter(
        (item: any) =>
          item.moduleName === "Return" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );

      setActiveReturnSequences(returnSequence || []);

      if (returnSequence && returnSequence.length > 0) {
        setHasReturnSequence(true);
        // Check if current site has a sequence
        const siteId = OriginalData?.siteId;
        const siteHasSequence = returnSequence.some(
          (seq: any) =>
            seq.siteId?.toString() === siteId?.toString() ||
            seq.level === "Org"
        );
        setReturnNumberingMode(siteHasSequence ? 'auto' : 'manual');
      } else {
        setHasReturnSequence(false);
        setReturnNumberingMode('manual');
      }
    } catch (error) {
      console.error("Error checking return sequence:", error);
      setHasReturnSequence(false);
      setActiveReturnSequences([]);
      setReturnNumberingMode('manual');
    }
  };

  const getCurrentReturnSequence = () => {
    const siteId = OriginalData?.siteId;
    if (!siteId || activeReturnSequences.length === 0) return null;

    const siteSequence = activeReturnSequences.find(
      (seq: any) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === siteId?.toString()
    );
    if (siteSequence) return siteSequence;

    const orgSequence = activeReturnSequences.find(
      (seq: any) => seq.level === "Org"
    );
    if (orgSequence) return orgSequence;

    return null;
  };

  // Auto-set return number prefix when in auto mode
  useEffect(() => {
    if (returnNumberingMode === 'auto' && returnsDrawerVisible) {
      const currentSequence = getCurrentReturnSequence();
      if (currentSequence) {
        const { prefix } = currentSequence;
        returnsForm.setFieldsValue({ returnNumber: prefix });
      }
    }
  }, [returnNumberingMode, activeReturnSequences, returnsDrawerVisible]);

  const handleReturnsAttachmentChange = (info: any) => {
    let files = info.fileList.slice(-3);
    setReturnsAttachments(files);
  };

  const beforeUpload = (file: any) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "File must be smaller than 10MB!",
      });
    }
    return false;
  };

  const columns = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text: any, record: any, index: number) => index + 1,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
      fixed: "left",
      render: (material: any) => <div className="align-left">{material}</div>,
    },
    {
      title: "Need By Date",
      dataIndex: "needByDate",
      key: "needByDate",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Qty Ordered",
      dataIndex: "quantityOrdered",
      key: "quantityOrdered",
      width: 150,
      render: (text: any, record: any) => (
        <div className="align-left">
          {Number(text)?.toFixed(2) + " " + record?.uom}
        </div>
      ),
    },
    {
      title: "Invoiced Qty",
      dataIndex: "quantityInvoiced",
      key: "quantityInvoiced",
      width: 150,
      render: (text: any, record: any) => (
        <div className="align-left">
          {Number(text)?.toFixed(2) + " " + record?.uom}
        </div>
      ),
    },
    {
      title: "Dispatched Qty",
      dataIndex: "quantityDispatched",
      key: "quantityDispatched",
      width: 150,
      render: (text: any, record: any) => (
        <div className="align-left">
          {Number(text)?.toFixed(2) + " " + record?.uom}
        </div>
      ),
    },
    {
      title: "Received Qty",
      dataIndex: "quantityDelivered",
      key: "quantityDelivered",
      width: 150,
      render: (text: any, record: any) => (
        <div className="align-left">
          {Number(text)?.toFixed(2) + " " + record?.uom}
        </div>
      ),
    },
    {
      title: "Return Qty",
      dataIndex: "returnsQuantity",
      key: "returnsQuantity",
      width: 150,
      editable: true,
      render: (text: any, record: any) => (
        <div className="align-left">
          {record?.isEligibleForReturn !== 1 ? (
            <span style={{ color: "#999", fontStyle: "italic" }}>
              Not Eligible
            </span>
          ) : text && Number(text) > 0 ? (
            Number(text)?.toFixed(2) + " " + record?.uom
          ) : (
            ""
          )}
        </div>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 150,
      render: (text: any, record: any) => (
        <p className="justify-end">
          {formatCurrency(Number(text), currency?.currencyCode)}
        </p>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "basicAmount",
      key: "basicAmount",
      width: 150,
      render: (text: any) => <div>{text}</div>,
    },
    // {
    //   title: "Net Amount",
    //   dataIndex: "netAmount",
    //   key: "netAmount",
    //   width: 150,
    //   render: (text: any) => <div>{text}</div>,
    // },
    {
      title: "CGST",
      children: [
        {
          title: "%",
          dataIndex: "cgstPercentage",
          key: "cgstPercentage",
          width: 100,
          render: (text: any, record: any) =>
            `${Number(record?.sgst) && Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
            }`,
        },
        {
          title: "Amount",
          dataIndex: "cgst",
          key: "cgst",
          width: 150,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              <span>
                {Number(record?.sgst) && Number(record?.cgst)
                  ? formatCurrency(text, "INR")
                  : "N/A"}
              </span>
            </div>
          ),
        },
      ],
    },
    {
      title: "SGST",
      children: [
        {
          title: "%",
          dataIndex: "sgstPercentage",
          key: "sgstPercentage",
          width: 100,
          render: (text: any, record: any) =>
            `${Number(record?.sgst) && Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
            }`,
        },
        {
          title: "Amount",
          dataIndex: "sgst",
          key: "sgst",
          width: 150,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              <span>
                {Number(record?.sgst) && Number(record?.cgst)
                  ? formatCurrency(text, "INR")
                  : "N/A"}
              </span>
            </div>
          ),
        },
      ],
    },
    {
      title: "IGST",
      children: [
        {
          title: "%",
          dataIndex: "igstPercentage",
          key: "igstPercentage",
          width: 100,
          render: (text: any, record: any) =>
            `${!Number(record?.sgst) && !Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
            }`,
        },
        {
          title: "Amount",
          dataIndex: "igst",
          key: "igst",
          width: 150,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              <span>
                {!Number(record?.sgst) && !Number(record?.cgst)
                  ? formatCurrency(text, "INR")
                  : "N/A"}
              </span>
            </div>
          ),
        },
      ],
    },
    {
      title: "Line Total",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: 150,
      render: (text: any) => <div>{text}</div>,
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   width: 150,
    //   render: (text: any) => <div>{text}</div>,
    // },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <div className="w-100 justify-content-around">
            <Button
              size="sm"
              color=""
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              <Icon name="save" />
            </Button>

            <Button size="sm" color="" onClick={cancel}>
              <Icon name="cross" />
            </Button>
          </div>
        ) : (
          <Button size="sm" color="" onClick={() => edit(record)}>
            <Icon name="edit" />
          </Button>
        );
      },
    },
  ];

  const mergedColumns: any = columns.map((col) => {
    if (
      ["basicAmount", "netAmount", "additionalCharges", "grossTotal"].includes(
        col?.dataIndex as string
      )
    ) {
      return {
        ...col,
        render: (text: any) => (
          <p className="justify-end">
            {formatCurrency(Number(text), currency?.currencyCode)}
          </p>
        ),
      };
    } else if ((col.children?.length as number) > 0) {
      return {
        ...col,
        // children: col?.children?.map((child: any) => {
        //   if (child?.title == "Amount") {
        //     return {
        //       ...child,
        //       render: (text: any) => (
        //         <p className="justify-end">
        //           {formatCurrency(
        //             Number(text),
        //             currency?.currencyCode
        //           )}
        //         </p>
        //       ),
        //     };
        //   } else {
        //     return child;
        //   }
        // }),
      };
    }
    if (
      materialData?.needByDateType != "Line Level" &&
      col?.dataIndex == "needByDate"
    ) {
      return {
        ...col,
        hidden: true,
      };
    }
    if (col?.dataIndex == "returnsQuantity") {
      // Check if any line item has returns
      const hasReturns = dataSource.some(
        (item: any) => Number(item?.quantityReturned || 0) > 0
      );

      return {
        ...col,
        hidden: !returnsMode && !hasReturns, // Show if in returns mode OR if returns exist
        onCell: (record: any): any => ({
          record,
          inputType: "number",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: false,
          returnsMode: returnsMode,
          selectedRowKeys: selectedRowKeys,
          onReturnQuantityChange: () => checkReturnQuantities(selectedRowKeys),
        }),
        render: (text: any, record: any) => {
          // In view mode (not returnsMode), show quantityReturned
          if (!returnsMode && hasReturns) {
            const returnedQty = Number(record?.quantityReturned || 0);
            const lineReturnNumber = getLineReturnNumber(record);
            return (
              <div className="d-flex align-items-center">
                {returnedQty > 0 ? (
                  <AntTooltip title="View return details">
                    <span
                      className="align-left text-primary"
                      role="button"
                      tabIndex={0}
                      style={{
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        handleViewReturns(lineReturnNumber);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          handleViewReturns(lineReturnNumber);
                        }
                      }}
                    >
                      {`${returnedQty.toFixed(2)} ${record?.uom}`}
                    </span>
                  </AntTooltip>
                ) : (
                  <span className="align-left">-</span>
                )}
              </div>
            );
          }
          // In returns mode, check if item is eligible
          const hasExistingReturn = Number(record?.quantityReturned || 0) > 0;
          const isRowSelected = selectedRowKeys?.includes(record?.lineNo);

          // Calculate return qty: (quantityDispatched - quantityDelivered)
          // Use quantityInvoiced as fallback if quantityDispatched doesn't exist
          const quantityDispatched = Number(record?.quantityDispatched || record?.quantityInvoiced || 0);
          const quantityDelivered = Number(record?.quantityDelivered || 0);
          const calculatedReturnQty = quantityDispatched - quantityDelivered;

          // Get form value if user has entered one (for selected rows)
          let userEnteredValue = 0;
          if (isRowSelected) {
            const formValue = form.getFieldValue(`returnsQuantity_${record?.lineNo}`);
            userEnteredValue = formValue ? Number(formValue) : 0;
          }

          return (
            <div className="align-left">
              {record?.isEligibleForReturn !== 1 || hasExistingReturn ? (
                <span style={{ color: "#999", fontStyle: "italic" }}>
                  Not Eligible
                </span>
              ) : isRowSelected && userEnteredValue > 0 ? (
                // Show user-entered value if exists for selected row
                userEnteredValue.toFixed(2) + " " + record?.uom
              ) : calculatedReturnQty > 0 ? (
                // Show calculated value for all eligible rows in returns mode
                calculatedReturnQty.toFixed(2) + " " + record?.uom
              ) : (
                ""
              )}
            </div>
          );
        },
      };
    }
    if (!col.editable) {
      return col;
    }
    // Disable editing for Invoiced Qty and Unit Price during returns mode
    if (
      returnsMode &&
      (col.dataIndex === "quantityInvoiced" || col.dataIndex === "unitPrice")
    ) {
      return {
        ...col,
        editable: false,
        onCell: undefined, // Remove onCell to prevent editing
        render: (text: any, record: any) => {
          // Remove onClick handler to prevent editing in returns mode
          if (col.dataIndex === "quantityInvoiced") {
            return (
              <div className="align-left">
                {Number(text)?.toFixed(2) + " " + record?.uom}
              </div>
            );
          } else if (col.dataIndex === "unitPrice") {
            return (
              <p className="justify-end">
                {formatCurrency(Number(text), currency?.currencyCode)}
              </p>
            );
          }
          return col.render ? col.render(text, record) : text;
        },
      };
    }
    return {
      ...col,
      onCell: (record: any): any => ({
        record,
        inputType: col.dataIndex === "quantity" ? "number" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        returnsMode: returnsMode,
      }),
    };
  });

  const FormatCurrency = (
    number: any,
    currencyFormat: any,
    currencySymbol: any
  ) => {
    if (currencyFormat == "") {
      currencyFormat = "₹#,##0.00";
    }
    if (currencySymbol == "") {
      currencySymbol = "₹";
    }
    const symbolBeforePattern = /^([^#0,]+)?([#0.,]+)$/;
    const symbolAfterPattern = /^([#0.,]+)([^#0,]+)?$/;
    let locale = "en-US"; // Default locale
    let symbolPosition = "before"; // Default to symbol before the number
    let minimumFractionDigits = 2; // Default to 2 decimal places
    let matchBefore = currencyFormat?.match(symbolBeforePattern);
    let matchAfter = currencyFormat?.match(symbolAfterPattern);
    if (matchBefore) {
      symbolPosition = "before";
    } else if (matchAfter) {
      symbolPosition = "after";
    }
    let fractionMatch = currencyFormat?.match(/[.,]0*$/);
    if (fractionMatch) {
      minimumFractionDigits = fractionMatch[0].length - 1;
    }
    let formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: minimumFractionDigits,
    });
    let formattedNumber = formatter.format(number);
    if (formattedNumber == "NaN" || formattedNumber == "undefined") {
      return "";
    }
    if (symbolPosition === "before") {
      return currencySymbol + formattedNumber;
    } else {
      return formattedNumber + currencySymbol;
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchHtmlContentFromUrl = async (url: string) => {
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "text/html",
        },
      });

      return response?.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const fetchInvoiceData = async () => {
    try {
      const response = await invoiceApis.fetchInvoice(invoiceNumber);
      const { status, message, data } = response;
      if (status) {
        if (data?.additionalChargesArray) {
          setAdditionalCharges(
            data?.additionalChargesArray?.map((charge: any) => {
              const id = nanoid(4);
              form2.setFieldsValue({
                [`AdditionalCharges.${id}.name`]: charge?.name,
                [`AdditionalCharges.${id}.price`]: Number(charge?.price),
              });
              return {
                ...charge,
                id,
              };
            })
          );
        }
        if (data?.invoiceLines) {
          setDataSource(data?.invoiceLines);
        }
        if (data?.dms?.length > 0) {
          const dms = data?.dms;
          setDms(dms);
          const _signature = dms?.find(
            (item: any) => item.moduleFileType == "signature"
          );
          if (_signature) {
            setSignature({
              docId: _signature?.docId,
              fileName: _signature?.fileName,
              image: _signature?.filePath,
              moduleFileType: _signature?.moduleFileType,
              module: _signature?.module,
              label: data?.signatureLabel,
              isActive: true,
            });
          }
          const _termsAndConditions = dms?.find(
            (item: any) =>
              item.moduleFileType == "termsAndConditions" &&
              item.module == "invoice"
          );
          if (_termsAndConditions) {
            const data: any = await fetchHtmlContentFromUrl(
              _termsAndConditions?.filePath
            );
            setTermsAndConditions(data);
            setNewTermsAndConditions(data);
          }

          const _DCAttachment = dms?.find(
            (item: any) =>
              (item.moduleFileType == "DCAttachment" ||
                item.moduleFileType == "attachment") &&
              item.module == "invoice"
          );
          if (_DCAttachment) {
            setDCAttachment(_DCAttachment);
          }

          const _InvoiceAttachment = dms?.find(
            (item: any) =>
              item.moduleFileType == "InvoiceAttachment" &&
              item.module == "invoice"
          );
          if (_InvoiceAttachment) {
            setInvoiceAttachment(_InvoiceAttachment);
          }
        }
        setCurrency({
          currencyCode: data?.currencyCode,
          currency: data?.currencyName,
          currencySymbol: data?.currencySymbol,
          currencyFormat: data?.currencyFormat,
        });
        // if (
        //   [
        //     "Pending Approval",
        //     "Approval Rejected",
        //     "To be Dispatched",
        //     "Partially Dispatched",
        //   ].includes(data.invoiceStatus) && data?.invoiceType!="commercial"
        // ) {
        //   setReadOnly(false);
        // } else {
        //   setReadOnly(true);
        // }
        await fetchDMSData("buyerOrgLogo", data?.orgId);
        await fetchDMSData("sellerOrgLogo", data?.supplierId);
        setMaterialData(data);
        setOriginalData(data);
        setShowSubmitBtn(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDMSData = async (type: string, id: any) => {
    try {
      const queryObj: any = {};
      switch (type) {
        case "buyerOrgLogo":
          queryObj["moduleFileType"] = "org logo";
          queryObj["module"] = "orgs";
          queryObj["orgId"] = id;
          break;
        case "sellerOrgLogo":
          queryObj["moduleFileType"] = "org logo";
          queryObj["module"] = "orgs";
          queryObj["orgId"] = id;
          break;
        case "termsAndConditions":
          queryObj["moduleFileType"] = "termsAndConditions";
          queryObj["module"] = "invoice";
          queryObj["invoiceNumber"] = id;
          break;
        default:
          return;
      }
      const _response = await miscService.getByQuery(queryObj);
      const { status, response: dmsData } = _response;
      if (status && dmsData?.length > 0) {
        const item = dmsData[0];
        switch (type) {
          case "buyerOrgLogo":
            let orgLogo = item?.filePath;
            setOriginalData((pre: any) => ({ ...pre, buyerOrgLogo: orgLogo }));
            break;
          case "sellerOrgLogo":
            let sellerOrgLogo = item?.filePath;
            setOriginalData((pre: any) => ({
              ...pre,
              sellerOrgLogo: sellerOrgLogo,
            }));
            break;
          case "termsAndConditions":
            const _termsAndConditions: any = await fetchHtmlContentFromUrl(
              item?.filePath
            );
            setTermsAndConditions(_termsAndConditions);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const SubmitChanges = async () => {
    try {
      const _formData = new FormData();

      const newfiledata: any = [];
      const deletedFileData: any = [];
      const updatedFileData: any = [];
      const _updatedLineItems: any = [];
      dataSource?.forEach((item: any) => {
        if (updatedLineItems?.includes(item?.lineNo)) {
          _updatedLineItems.push({
            lineNo: Number(item?.lineNo),
            materialId: Number(item?.materialId),
            quantityInvoiced: Number(item?.quantityInvoiced),
          });
        }
      }, []);

      if (isTermsAndConditionsChanged) {
        const termsAndConditionsFile = new Blob([newTermsAndConditions], {
          type: "text/html",
        });
        const _termsAndConditions = dms?.find(
          (item: any) => item.moduleFileType == "termsAndConditions"
        );
        if (_termsAndConditions?.docId) {
          deletedFileData.push({
            docId: _termsAndConditions.docId,
          });
        }
        _formData.append(
          `files`,
          new File([termsAndConditionsFile], "termsAndConditions.html", {
            type: "text/html",
          })
        );
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "termsAndConditions",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        setIsTermsAndConditionsChanged(false);
        setNewTermsAndConditions(null);
      }
      if (newDCAttachment) {
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "DCAttachment",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        if (DCAttachment)
          deletedFileData.push({
            docId: DCAttachment?.docId,
          });
        _formData.append(
          `files`,
          new File([newDCAttachment], newDCAttachment?.name, {
            type: newDCAttachment?.type,
          })
        );
        setNewDCAttachment(null);
      }
      if (newInvoiceAttachment) {
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "InvoiceAttachment",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        if (InvoiceAttachment)
          deletedFileData.push({
            docId: InvoiceAttachment?.docId,
          });
        _formData.append(
          `files`,
          new File([newInvoiceAttachment], newInvoiceAttachment?.name, {
            type: newInvoiceAttachment?.type,
          })
        );
        setNewInvoiceAttachment(null);
      }

      const filesData = {
        newfiledata,
        deletedFileData,
        updatedFileData,
      };
      const invoiceData: any = {
        mode: "U",
        combinedLineItems: {
          existingLineItems: OriginalData?.invoiceLines?.map((item: any) => {
            return {
              lineNo: item?.lineNo,
              materialId: item?.materialId,
              quantityInvoiced: Number(item?.quantityInvoiced),
            };
          }),
          updatedLineItems: _updatedLineItems,
          newLineItems: [],
          deletedLineItems: [],
        },
        additionalChargesArray: additionalCharges?.map((charge: any) => {
          return {
            name: charge?.name,
            price: charge?.price,
          };
        }),
      };
      if (newInvoiceType != "") {
        invoiceData.invoiceType = newInvoiceType;
        setNewInvoiceType("");
      }
      if (newInvoiceDate) {
        invoiceData.invoiceDate = newInvoiceDate?.format("YYYY-MM-DD");
        setNewInvoiceDate(null);
      }
      if (newDueDate) {
        invoiceData.dueDate = newDueDate?.format("YYYY-MM-DD");
        setNewDueDate(null);
      }
      _formData.append("invoiceData", JSON.stringify(invoiceData));
      _formData.append("filesData", JSON.stringify(filesData));
      _formData.append("files", JSON.stringify([]));
      // const response = await invoiceApis.amendmentInvoice(
      //   invoiceNumber,
      //   _formData
      // );
      // const { status, data, message } = response;
      // if (status) {
      //   Swal.fire({
      //     icon: "success",
      //     title: "Successfully Updated",
      //     showConfirmButton: false,
      //     timer: 1000,
      //   });

      //   fetchInvoiceData();
      // } else {
      //   Swal.fire({
      //     icon: "error",
      //     title: message,
      //     showConfirmButton: false,
      //     timer: 1000,
      //   });
      // }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        showConfirmButton: false,
        timer: 1000,
      });
      console.log(error);
    }
  };

  useEffect(() => {
    // fetchInvoiceData();
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    if (invoiceNumber) {
      setIsLoading(true);
      fetchInvoiceData();
    }
  }, [invoiceNumber]);

  const ShowBillingAddress = ({ address }: any) => {
    if (!address) return <></>;
    const {
      deliveryLocationName,
      addressLine1,
      addressLine2,
      city,
      state,
      countryInfo,
      pincode,
      district,
    } = address;
    const { cin, gstin, pan, phoneNumber } = OriginalData?.orgDetails;
    return (
      <ul>
        <li>
          <strong style={{ color: "#2263b3" }}>
            {address?.deliveryLocationName}
          </strong>
        </li>
        <li>{addressLine1}</li>
        <li>{addressLine2}</li>
        <li>
          {city},{district}, {state}, {countryInfo} - {pincode}
        </li>
        <li>
          <strong>GSTIN</strong> : {gstin}
        </li>
        <li>
          <strong>CIN</strong> : {cin}
        </li>
        <li>
          <strong>PAN</strong> : {pan}
        </li>
        <li>
          <strong>Phone</strong> : {phoneNumber}
        </li>
      </ul>
    );
  };
  const ShowDeliveryAddress = ({ address }: any) => {
    if (!address) return <></>;
    const {
      deliveryLocationName,
      addressLine1,
      addressLine2,
      city,
      state,
      countryInfo,
      pincode,
      district,
    } = address;

    return (
      <ul>
        <li>
          <strong style={{ color: "#2263b3" }}>
            {address?.deliveryLocationName}
          </strong>
        </li>
        <li>
          {addressLine1},{addressLine2}
        </li>
        <li>
          {city},{district}, {state}, {countryInfo} - {pincode}
        </li>
        <li>
          <strong>Contact Person</strong> : {address?.contactPersonName}
        </li>
        <li>
          <strong>Mobile Number</strong> : {address?.mobileNumber}
        </li>
      </ul>
    );
  };

  const ViewTableTotals = ({ data, additionalCharges, invoice }: any) => {
    let totalNetAmmount = 0,
      cgst = 0,
      sgst = 0,
      igst = 0,
      TotalAmount = 0;
    data?.forEach((item: any) => {
      const totalAmmount =
        Number(item.unitPrice) * Number(item.quantityInvoiced);
      cgst = cgst + (totalAmmount * Number(item.cgstPercentage)) / 100;
      sgst = sgst + (totalAmmount * Number(item.sgstPercentage)) / 100;
      igst = igst + (totalAmmount * Number(item.igstPercentage)) / 100;
      totalNetAmmount = totalNetAmmount + totalAmmount;
    });
    TotalAmount = totalNetAmmount + cgst + sgst + igst;
    additionalCharges?.forEach((item: any) => {
      TotalAmount = TotalAmount + Number(item.price);
    });
    return (
      <Row
        className={`${invoice && "w-50 mt-2"}`}
        style={{
          margin: "20px",
        }}
      >
        <Col className={`justify-end`}>
          <div className={`${invoice ? "w-100" : "w-40"}`}>
            <div className="d-flex justify-content-between">
              <div className="w-50 justify-end">
                <strong>Taxable Amount</strong>
              </div>
              <span className="r_align">
                {formatCurrency(totalNetAmmount, currency?.currencyCode)}
              </span>
            </div>
            {cgst && sgst ? (
              <>
                <div className="d-flex justify-content-between">
                  <div className="w-50 justify-end">
                    <strong>CGST</strong>
                  </div>
                  <span className="r_align">
                    {formatCurrency(cgst, currency?.currencyCode)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="w-50 justify-end">
                    <strong>SGST</strong>
                  </div>
                  <span className="r_align">
                    {formatCurrency(sgst, currency?.currencyCode)}
                  </span>
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-between">
                <div className="w-50 justify-end">
                  <strong>IGST</strong>
                </div>
                <span className="r_align">
                  {formatCurrency(igst, currency?.currencyCode)}
                </span>
              </div>
            )}
            {additionalCharges?.map((item: any) => {
              if (!item.price || !item.price) return null;
              return (
                <div className="d-flex justify-content-between">
                  <div className="w-50 justify-end">
                    <strong>{item.name}</strong>
                  </div>
                  <span className="r_align">
                    {formatCurrency(item.price, currency?.currencyCode)}
                  </span>
                </div>
              );
            })}
            <div
              className="d-flex justify-content-between"
              style={{ fontSize: "18px", marginTop: "10px" }}
            >
              <div className="w-50 justify-end">
                <strong>Total</strong>
              </div>
              <strong className="r_align">
                {formatCurrency(TotalAmount, currency?.currencyCode)}
              </strong>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = materialData?.invoiceApprovals;
    const isStrictMode = materialData?.isStrictApproval || false;

    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.user?.firstName}
                  <div className="designation">{approval?.role?.roleName}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>
          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  const dcFileUploadProps = {
    beforeUpload: (file: any) => {
      // Perform any file validation or processing here
      setNewDCAttachment(file);
      setShowSubmitBtn(true);
      return false; // Return false to prevent auto-upload
    },
    maxCount: 1,
    onRemove: () => {
      setNewDCAttachment(null); // Clear the file from state
    },
  };
  const invoiceFileUploadProps = {
    beforeUpload: (file: any) => {
      // Perform any file validation or processing here
      setShowSubmitBtn(true);
      setNewInvoiceAttachment(file);
      return false; // Return false to prevent auto-upload
    },
    maxCount: 1,
    onRemove: () => {
      setNewInvoiceAttachment(null); // Clear the file from state
    },
  };

  const ShowSupplierBankInfo = () => {
    if (!OriginalData?.supplierDetails) return <></>;

    const { cin, gstin, pan, phoneNumber, bankDetails, address } =
      OriginalData?.supplierDetails || {};

    const { bankName, accountNumber, accountHolderName, branch, ifscCode } =
      bankDetails || {};

    const { city, stateInfo, countryInfo, pincode } = address || {};

    return (
      <Descriptions
        title="Supplier Bank Details"
        bordered
        column={1}
        size="small"
      >
        <Descriptions.Item label="GSTIN">{gstin}</Descriptions.Item>
        <Descriptions.Item label="CIN">{cin}</Descriptions.Item>
        <Descriptions.Item label="PAN">{pan}</Descriptions.Item>
        <Descriptions.Item label="Phone">{phoneNumber}</Descriptions.Item>
        <Descriptions.Item label="Bank Name">{bankName}</Descriptions.Item>
        <Descriptions.Item label="Account Number">
          {accountNumber}
        </Descriptions.Item>
        <Descriptions.Item label="Company Account Name">
          {accountHolderName}
        </Descriptions.Item>
        <Descriptions.Item label="Branch">{branch}</Descriptions.Item>
        <Descriptions.Item label="IFSC Code">{ifscCode}</Descriptions.Item>
        <Descriptions.Item label="City">{city}</Descriptions.Item>
        <Descriptions.Item label="State">
          {stateInfo?.stateName}
        </Descriptions.Item>
        <Descriptions.Item label="Country">
          {countryInfo?.countryName}
        </Descriptions.Item>
        <Descriptions.Item label="Pincode">{pincode}</Descriptions.Item>
      </Descriptions>
    );
  };

  const ApproveInvoice = async () => {
    // try {
    //   const response = await invoiceApis.approveInvoice(invoiceNumber);
    //   if (response.status) {
    //     Swal.fire("Approved!", "Invoice has been approved", "success");
    //   }
    // } catch (error) {}
  };

  const DisapproveInvoice = async (reasonForRejection: string) => {
    try {
      //   const response = await invoiceApis.disapproveInvoice(
      //     invoiceNumber,
      //     reasonForRejection
      //   );
      //   if (response.status) {
      //     Swal.fire("Rejected!", "Invoice has been rejected", "success");
      //   }
    } catch (error) { }
  };

  const returnBillingDetails = () => {
    if (!OriginalData?.billingAddress) {
      return {};
    }
    const {
      deliveryLocationName,
      addressLine1,
      addressLine2,
      city,
      state,
      countryInfo,
      pincode,
      district,
    } = OriginalData?.billingAddress;
    const { cin, gstin, pan, phoneNumber } = OriginalData?.orgDetails;
    return {
      name: OriginalData?.orgName || "",
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      city: city,
      state: state,
      pincode: pincode,
      country: countryInfo,
      gstin: gstin || "",
      pan: pan || "",
      cin: cin || "",
    };
  };

  const returnSupplierDetails = () => {
    if (!OriginalData?.supplierDetails) {
      return {};
    }
    const { cin, gstin, pan, phoneNumber, bankDetails, address } =
      OriginalData?.supplierDetails || {};

    const { bankName, accountNumber, accountHolderName, branch, ifscCode } =
      bankDetails || {};

    const {
      city,
      stateInfo,
      countryInfo,
      pincode,
      addressline1,
      addressline2,
    } = address || {};
    return {
      name: OriginalData?.supplierName || "",
      addressLine1: addressline1 || "",
      addressLine2: addressline2 || "",
      city: city || "",
      state: stateInfo?.stateName || "",
      pincode: pincode || "",
      country: countryInfo?.countryName || "",
      gstin: gstin || "",
      pan: pan || "",
      cin: cin || "",
      bankName: bankName || "",
      accountNumber: accountNumber || "",
      accountHolderName: accountHolderName || "",
      branch: branch || "",
      ifscCode: ifscCode || "",
    };
  };
  const returnInvoiceDetails = () => {
    let deliveryAddressString = "";
    if (
      OriginalData?.deliveryAddressType != "Line Level" &&
      OriginalData?.deliveryAddress
    ) {
      const {
        deliveryLocationName,
        addressLine1,
        addressLine2,
        city,
        state,
        countryInfo,
        pincode,
        district,
      } = OriginalData?.deliveryAddress;
      deliveryAddressString = [
        deliveryLocationName?.toUpperCase(),
        addressLine1,
        addressLine2,
        city + ", " + state + ", " + district,
        countryInfo + ", " + pincode,
      ]
        ?.filter(Boolean)
        .join("\n");
    } else {
      deliveryAddressString = "Line Level";
    }

    return {
      invoiceNumber: OriginalData?.invoiceId,
      invoiceDate: dayjs(OriginalData?.invoiceDate).format("DD-MMM-YYYY"),
      dueDate: dayjs(materialData?.dueDate).format("DD-MMM-YYYY"),
      terms:
        materialData?.paymentTerms +
        (materialData?.advancePercentage
          ? `(${materialData?.advancePercentage}%)`
          : ""),
      customSONumber: "",
      ewayBillNumber: OriginalData?.ewbNo || "",
      placeofSupply: OriginalData?.deliveryAddress?.city || "",
      creditDays:
        materialData?.paymentTerms != "Cash and Carry"
          ? materialData?.creditDays
          : 0,
      deliveryAddressString,
      vehicleRefNo: "N/A",
      transporterName:
        OriginalData?.siteContactPersonDetails?.contactPersonName,
      driverNumber:
        OriginalData?.siteContactPersonDetails?.contactPersonMobileNumber,
      orderNumber: OriginalData?.orderId,
      orderDate: dayjs(OriginalData?.orderDate).format("DD-MMM-YYYY"),

      totals: {
        taxableAmount: formatDecimal(materialData?.netAmount),
        cgst: formatDecimal(materialData?.cgst),
        sgst: formatDecimal(materialData?.sgst),
        igst: formatDecimal(materialData?.igst),
        total: formatDecimal(
          Number(materialData?.grossTotal) +
          Number(materialData?.additionalCharges)
        ),
      },
      additionalCharges,
      lines: dataSource?.map((item: any, index: any) => {
        let itemDescription = item?.materialName;
        if (item?.description) {
          itemDescription += "\t" + item?.description;
        }
        let deliveryAddressString = "";
        if (
          OriginalData?.deliveryAddressType == "Line Level" &&
          item?.deliveryAddress
        ) {
          const {
            deliveryLocationName,
            addressLine1,
            addressLine2,
            city,
            state,
            countryInfo,
            pincode,
            district,
          } = OriginalData?.deliveryAddress;
          deliveryAddressString = [
            deliveryLocationName?.toUpperCase(),
            addressLine1,
            addressLine2,
            city,
            state,
            countryInfo,
            pincode,
            district,
          ]
            ?.filter(Boolean)
            .join(",");
          itemDescription += "\n\nDelivery Address: " + deliveryAddressString;
        }
        return {
          sno: index + 1,
          itemDescription,
          hsnSac: item?.hsnCode || "",
          rate: formatDecimal(item?.unitPrice),
          cgstPercentage: Number(item?.cgstPercentage),
          cgstAmount: formatDecimal(item?.cgst),
          sgstPercentage: Number(item?.sgstPercentage),
          sgstAmount: formatDecimal(item?.sgst),
          igstPercentage: Number(item?.igstPercentage),
          igstAmount: formatDecimal(item?.igst),
          taxableAmount: formatDecimal(item?.grossTotal),
          qty: Number(item?.quantityInvoiced),
          uom: item?.uom,
        };
      }),
    };
  };

  return (
    <div className="card-inner">
      {sm && mobileView && (
        <div className="toggle-overlay" onClick={() => setSm(!sm)}></div>
      )}
      <BlockHead size="lg">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h4">
              <span>
                PO #{OriginalData?.orderId} | Invoice #{OriginalData?.invoiceId}{" "}
              </span>
            </BlockTitle>
          </BlockHeadContent>

          <RejectInvoice
            isModalVisible={invoiceDisapproveInvoice}
            setIsModalVisible={setInvoiceDisapproveInvoice}
            rejectInvoice={DisapproveInvoice}
          />

          <div className=" d-flex">
            {["Pending Approval"].includes(OriginalData?.invoiceStatus) &&
              !readOnly &&
              !showSubmitBtn && (
                <div className="actions clearfix">
                  <ul
                    className="m-auto d-flex align-items-center justify-content-center p-0"
                    style={{
                      flexDirection: "row-reverse",
                    }}
                  >
                    <li className="pt-0 pb-0">
                      <Button
                        color="success"
                        type="button"
                        onClick={() => {
                          ApproveInvoice();
                        }}
                      >
                        Approve
                      </Button>
                    </li>
                    <li className="pt-0 pb-0">
                      <Button
                        color="danger"
                        onClick={() => {
                          setInvoiceDisapproveInvoice(true);
                        }}
                        type="button"
                        className="btn-dim btn btn-secondary"
                      >
                        Reject
                      </Button>
                    </li>
                  </ul>
                </div>
              )}
            {showSubmitBtn && !readOnly ? (
              <div className="actions clearfix paddingLeft10">
                <ul
                  className="m-auto d-flex align-items-center justify-content-center p-0"
                  style={{
                    flexDirection: "row-reverse",
                  }}
                >
                  <li className="pt-0 pb-0">
                    <Button
                      color="primary"
                      type="button"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "Do you want to submit the changes?",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, submit it!",
                          cancelButtonText: "No, cancel",
                        }).then(async (result) => {
                          if (result.isConfirmed) {
                            await SubmitChanges();
                          }
                        });
                      }}
                    >
                      Save Changes
                    </Button>
                  </li>
                  <li className="p-0">
                    <Button
                      color="secondary"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "Do you want to revert the changes?",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, revert it!",
                          cancelButtonText: "No, cancel",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            fetchInvoiceData();
                            Swal.fire(
                              "Reverted!",
                              "Your changes have been reverted.",
                              "success"
                            );
                          }
                        });
                      }}
                      type="button"
                      className="btn-dim btn btn-secondary"
                    >
                      Revert Changes
                    </Button>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="d-flex">
                <div className="paddingLeft10">
                  <Button
                    className="toggle"
                    color="primary"
                    onClick={async () => {
                      navigate(previousPath, {
                        state: { previousState: extra?.previousState },
                      });
                    }}
                  >
                    <Icon name="arrow-left" />
                    <span>Back</span>
                  </Button>
                </div>

                <ul className="nk-tb-actions ms-2">
                  <li>
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="dropdown-toggle btn btn-icon btn-trigger"
                      >
                        <Icon name="more-v"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <ul className="link-list-opt no-bdr">
                          {materialData?.isEligibleForReturn === 1 && canCreateReturn && (
                            <li
                              onClick={(e) => {
                                e.preventDefault();
                                setReturnsMode(true);
                                setSelectedRowKeys([]);
                                setEditingKey(null);
                                // Close the dropdown
                                const dropdown = document.querySelector(
                                  ".dropdown-menu.show"
                                );
                                if (dropdown) {
                                  dropdown.classList.remove("show");
                                }
                              }}
                            >
                              <Link to={`#`}>
                                <Icon name="package" /> Create Return
                              </Link>
                            </li>
                          )}
                          <li>
                            <PdfGenerator
                              orgDetails1={returnSupplierDetails()}
                              orgDetails2={returnBillingDetails()}
                              HeaderDetails={returnInvoiceDetails()}
                              buyerOrgLogo={OriginalData?.sellerOrgLogo}
                              hasIGST={Number(materialData?.igst) != 0}
                              TermsAndConditions={termsAndConditions}
                              eInvoiceData={OriginalData?.eInvoiceData}
                            />
                          </li>
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </BlockBetween>
      </BlockHead>
      {isLoading == true ? (
        <>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </>
      ) : (
        <div className="content clearfix">
          <AntCollapse defaultActiveKey={["1", "2", "3"]}>
            <Panel header="General Information" key="1">
              <div className="tab-content mt-0">
                <div className={`tab-pane${tab === "1" && " active"}`}>
                  <Col className="justify-start w-100  flex-wrap  p-0">
                    <div className="w-30 p-1 pt-0 pb-0 border-end">
                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-30">Invoice Type</div>
                        <div className="data-value w-70">
                          {materialData?.invoiceType != "commercial" &&
                            !readOnly ? (
                            <Form.Item
                              name="invoiceType"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.invoiceType}
                            >
                              <Select
                                placeholder="Select Invoice Type"
                                defaultValue={materialData?.invoiceType}
                                onChange={(e) => {
                                  setNewInvoiceType(e);
                                  setShowSubmitBtn(true);
                                }}
                              >
                                {materialData?.invoiceType == "dc" ? (
                                  <Option value="dc">Delivery Challan</Option>
                                ) : (
                                  <Option value="proforma">
                                    Proforma Invoice
                                  </Option>
                                )}
                                <Option value="commercial">
                                  Commercial Invoice
                                </Option>
                              </Select>
                            </Form.Item>
                          ) : (
                            <span>
                              {materialData?.invoiceType == "commercial"
                                ? "Commercial Invoice"
                                : materialData?.invoiceType == "dc"
                                  ? "Delivery Challan"
                                  : "Proforma Invoice"}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-30">Invoice Date</div>
                        <div className="data-value w-70">
                          {!readOnly ? (
                            <Form.Item
                              name="invoiceDate"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.invoiceDate}
                            >
                              <DatePicker
                                className="w-100"
                                placeholder="Invoice Due Date"
                                maxDate={dayjs().add(1, "day")}
                                allowClear={false}
                                defaultValue={dayjs(materialData?.invoiceDate)}
                                onChange={(e) => {
                                  setNewInvoiceDate(e);
                                  setShowSubmitBtn(true);
                                }}
                              />
                            </Form.Item>
                          ) : (
                            <span>
                              {dayjs(materialData?.invoiceDate).format(
                                "DD-MMM-YYYY"
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-30">Due Date</div>
                        <div className="data-value w-70">
                          {!readOnly ? (
                            <Form.Item
                              name="dueDate"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.dueDate}
                            >
                              <DatePicker
                                className="w-100"
                                placeholder="Invoice Due Date"
                                defaultValue={dayjs(materialData?.dueDate)}
                                allowClear={false}
                                onChange={(e) => {
                                  setNewDueDate(e);
                                  setShowSubmitBtn(true);
                                }}
                                minDate={
                                  newInvoiceDate
                                    ? newInvoiceDate
                                    : dayjs(materialData?.invoiceDate)
                                }
                              />
                            </Form.Item>
                          ) : (
                            <span>
                              {dayjs(materialData?.dueDate).format(
                                "DD-MMM-YYYY"
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Approvals</div>
                        <div className="data-value w-70">
                          <ApprovalTimelineContent />
                        </div>
                      </div>
                    </div>

                    <div className="w-30  paddingLeft10 border-end">
                      <div className=" pt-0">
                        <div className="data-label w-90 mb-1">
                          Billing Address
                        </div>
                        <div className="data-value w-90">
                          <ShowBillingAddress
                            address={materialData?.billingAddress}
                          />
                        </div>
                      </div>
                    </div>
                    {materialData?.deliveryAddressId && (
                      <div className="w-40 paddingLeft10 ">
                        <div className=" pt-0">
                          <div className="data-label w-9 mb-1">
                            Delivery Address
                          </div>
                          <div className="data-value w-90">
                            <ShowDeliveryAddress
                              address={materialData?.deliveryAddress}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Col>
                  {materialData?.irn && (
                    <div className="data-col align-center p-0 pt-1">
                      <div className="data-label" style={{ width: "120px" }}>
                        IRN # :
                      </div>
                      <div className="data-value w-90">{materialData?.irn}</div>
                    </div>
                  )}
                  {materialData?.eInvoiceData?.AckDt && (
                    <div className="data-col align-center p-0 pt-1">
                      <div className="data-label" style={{ width: "120px" }}>
                        Ack. Date :
                      </div>
                      <div className="data-value w-90">
                        {dayjs(materialData?.eInvoiceData?.AckDt)?.format(
                          "DD-MMM-YYYY"
                        )}
                      </div>
                    </div>
                  )}
                  {materialData?.eInvoiceData?.AckNo && (
                    <div className="data-col align-center p-0 pt-1">
                      <div className="data-label" style={{ width: "120px" }}>
                        Ack. No. :
                      </div>
                      <div className="data-value w-90">
                        {materialData?.eInvoiceData?.AckNo}
                      </div>
                    </div>
                  )}

                  {materialData?.eInvoiceStatus == "Y" &&
                    materialData?.eInvoiceCancelStatus == "Y" && (
                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label" style={{ width: "120px" }}>
                          Status :
                        </div>
                        <div className="data-value w-90">Cancelled</div>
                      </div>
                    )}
                  {materialData?.eInvoiceStatus == "Y" &&
                    materialData?.eInvoiceCancelStatus == "N" && (
                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label" style={{ width: "120px" }}>
                          Status :
                        </div>
                        <div className="data-value w-90">Acknowledged</div>
                      </div>
                    )}
                  {materialData?.ewbNo && (
                    <div className="data-col align-center p-0 pt-1">
                      <div className="data-label" style={{ width: "120px" }}>
                        E-Way Bill # :
                      </div>
                      <div className="data-value w-90">
                        {materialData?.ewbNo}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Panel>
            <Panel header="Material Information" key="2">
              {returnsMode && (
                <div className="mb-3 d-flex justify-content-end">
                  <Button
                    color="secondary"
                    style={{ height: 40, marginRight: 10 }}
                    onClick={() => {
                      setReturnsMode(false);
                      setSelectedRowKeys([]);
                      setHasReturnQuantities(false);
                      setHasInvalidReturnQty(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    style={{ height: 40 }}
                    onClick={showReturnsDrawer}
                    disabled={
                      selectedRowKeys.length === 0 ||
                      !hasReturnQuantities ||
                      hasInvalidReturnQty
                    }
                  >
                    Preview
                  </Button>
                </div>
              )}
              <div className="table-responsive poTable">
                <div
                  style={{
                    width: screenSize - (extra?.reduceTableWidth || 380) + "px",
                    margin: "0px auto",
                  }}
                >
                  <Form form={form} component={false}>
                    <Table
                      className="customTable"
                      columns={mergedColumns.map((col: any) => {
                        if (col.key == "actions") {
                          return {
                            ...col,
                            hidden: readOnly || returnsMode,
                          };
                        }
                        return col;
                      })}
                      rowSelection={returnsMode ? rowSelection : undefined}
                      components={{
                        body: {
                          cell: EditableCell,
                        },
                      }}
                      dataSource={dataSource.map((item: any) => ({
                        ...item,
                        quantityInvoiced: Number(item?.quantityInvoiced),
                        unitPrice: Number(item?.unitPrice),
                        key: item?.lineNo,
                      }))}
                      scroll={{ x: 1000 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <div className="expandableBody">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <p style={{ margin: 0 }}>{record.description}</p>
                              {record.file && (
                                <div>
                                  <PaperClipOutlined /> &nbsp;
                                  <a
                                    href={record?.file?.fileName}
                                    download={record?.file?.fileName}
                                  >
                                    {record?.file?.fileName}
                                  </a>
                                </div>
                              )}
                            </div>
                            {materialData?.deliveryAddressType ===
                              "Line Level" &&
                              record.deliveryAddressId && (
                                <div
                                  className="lineAddress"
                                  style={{ flex: 1 }}
                                >
                                  <h6>Delivery Address</h6>
                                  <ShowDeliveryAddress
                                    address={record.deliveryAddress}
                                  />
                                </div>
                              )}
                          </div>
                        ),
                      }}
                      bordered
                      size="middle"
                      pagination={false}
                    />
                  </Form>
                </div>
                <div className="d-flex">
                  <Form form={form2} component={false}>
                    <Row className="mt-2 w-50">
                      {!readOnly && (
                        <div>
                          <div className="d-flex align-items-center">
                            <h6 className="m-1 mb-0">
                              Additional Charges (If Any)
                            </h6>
                            <div className="w-20 m-1 mb-0">
                              {editAdditionalCharges ? (
                                <a
                                  type={undefined}
                                  onClick={() => {
                                    if (
                                      additionalCharges.find(
                                        (field: any, ind: number) => {
                                          const name = form2.getFieldValue(
                                            `AdditionalCharges.${field?.id}.name`
                                          );
                                          const price = form2.getFieldValue(
                                            `AdditionalCharges.${field?.id}.price`
                                          );
                                          return !name || !price;
                                        }
                                      )
                                    ) {
                                      return;
                                    }

                                    setAdditionalCharges([
                                      ...additionalCharges,
                                      {
                                        name: "",
                                        price: null,
                                        id: nanoid(4),
                                      },
                                    ]);
                                  }}
                                  className="addNewItem"
                                >
                                  <PlusSquareOutlined />
                                </a>
                              ) : (
                                <a
                                  type={undefined}
                                  onClick={() => {
                                    setEditAdditionalCharges(true);
                                  }}
                                  className="addNewItem"
                                >
                                  <EditOutlined />
                                </a>
                              )}
                            </div>
                          </div>
                          {additionalCharges.map(
                            (field: any, index: number) => {
                              return (
                                <Row key={field.id} className="m-2 mb-0 w-100">
                                  <div className="w-50">
                                    {editAdditionalCharges ? (
                                      <Form.Item
                                        name={`AdditionalCharges.${field.id}.name`}
                                        label={
                                          <strong>
                                            Additional Charge Name
                                          </strong>
                                        }
                                        rules={[
                                          {
                                            required: true,
                                            message: "Charge Name is required",
                                          },
                                        ]}
                                        className="mb-0"
                                        initialValue={field.name}
                                      >
                                        <Input
                                          className="w-100"
                                          disabled={!editAdditionalCharges}
                                          autoComplete="off"
                                          defaultValue={field.name}
                                        />
                                      </Form.Item>
                                    ) : (
                                      <strong>{field.name}</strong>
                                    )}
                                  </div>
                                  <div className="w-30">
                                    {editAdditionalCharges ? (
                                      <Form.Item
                                        name={`AdditionalCharges.${field.id}.price`}
                                        label={<strong>Price</strong>}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Price is required",
                                          },
                                        ]}
                                        className="mb-0"
                                        initialValue={Number(field.price)}
                                      >
                                        <InputNumber
                                          min={0}
                                          disabled={!editAdditionalCharges}
                                          defaultValue={Number(field.price)}
                                          className="w-100"
                                          formatter={(value) =>
                                            `${value}`.replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ","
                                            )
                                          }
                                          parser={(value: any) =>
                                            value!.replace(/\$\s?|(,*)/g, "")
                                          }
                                          onKeyPress={(event) => {
                                            if (!/^[0-9]+$/.test(event.key)) {
                                              event.preventDefault();
                                            }
                                          }}
                                          autoComplete="off"
                                        />
                                      </Form.Item>
                                    ) : (
                                      <strong>
                                        {formatCurrency(
                                          field.price,
                                          currency?.currencyCode
                                        )}
                                      </strong>
                                    )}
                                  </div>
                                  <Col className="d-flex align-items-end w-15">
                                    {editAdditionalCharges && (
                                      <a
                                        href="#dropdownitem"
                                        onClick={() => {
                                          form2.setFieldsValue({
                                            [`AdditionalCharges.${field.id}.name`]:
                                              "",
                                            [`AdditionalCharges.${field.id}.price`]:
                                              null,
                                          });
                                          const newAdditionalCharges: any = [];
                                          additionalCharges.forEach(
                                            (fld: any, ind: number) => {
                                              if (fld.id != field.id) {
                                                const name =
                                                  form2.getFieldValue(
                                                    `AdditionalCharges.${fld.id}.name`
                                                  );
                                                const price =
                                                  form2.getFieldValue(
                                                    `AdditionalCharges.${fld.id}.price`
                                                  );
                                                if (name && price) {
                                                  newAdditionalCharges.push({
                                                    name,
                                                    price,
                                                    id: fld.id,
                                                  });
                                                }
                                              }
                                            }
                                          );
                                          setAdditionalCharges(
                                            newAdditionalCharges
                                          );
                                          setShowSubmitBtn(true);
                                        }}
                                        className="addNewItem"
                                      >
                                        <DeleteOutlined />
                                      </a>
                                    )}
                                  </Col>
                                </Row>
                              );
                            }
                          )}
                          {additionalCharges.length > 0 &&
                            editAdditionalCharges && (
                              <Button
                                className="m-4 mt-3 mb-0"
                                color="primary"
                                onClick={() => {
                                  const newAdditionalCharges: any = [];
                                  additionalCharges.forEach(
                                    (field: any, index: number) => {
                                      const name = form2.getFieldValue(
                                        `AdditionalCharges.${field?.id}.name`
                                      );
                                      const price = form2.getFieldValue(
                                        `AdditionalCharges.${field?.id}.price`
                                      );
                                      console.log(field?.id, name, price);

                                      if (name && price) {
                                        newAdditionalCharges.push({
                                          name,
                                          price,
                                          id: field?.id,
                                        });
                                      }
                                    }
                                  );
                                  setAdditionalCharges(() => {
                                    return newAdditionalCharges;
                                  });
                                  if (newAdditionalCharges.length != 0) {
                                    setEditAdditionalCharges(false);
                                  }

                                  setShowSubmitBtn(true);
                                }}
                              >
                                Save
                              </Button>
                            )}
                        </div>
                      )}
                    </Row>
                  </Form>

                  <ViewTableTotals
                    data={dataSource}
                    additionalCharges={additionalCharges}
                    invoice={true}
                  />
                </div>
                <Row className="m-4 justify-content-end ">
                  {signature?.isActive && (
                    <div className="signatureBtnWrapper">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong>Signature</strong>
                      </div>
                      <div
                        style={{
                          minHeight: signature?.image ? "0px" : "120px",
                          display: signature?.image ? "block" : "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {signature?.image && (
                          <>
                            <div className="signatueImgWrapper border">
                              <img
                                src={signature?.image}
                                alt="Signed"
                                style={{ width: "160px" }}
                              />
                            </div>

                            <div>
                              <div className="form-group">
                                <label
                                  className="form-label"
                                  htmlFor="signatureLabel"
                                >
                                  Signature Label
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="signatureLabel"
                                  onChange={(e) => { }}
                                  disabled
                                  defaultValue={
                                    signature?.label || "Authorised Signatory"
                                  }
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </Row>
              </div>
            </Panel>
            <Panel header="Other Information" key="3">
              <div className="w-100 d-flex">
                <Descriptions
                  title=""
                  className="custom-description-title w-30"
                >
                  <Descriptions.Item label="Terms and Conditions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setTermsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                </Descriptions>

                <div className="w-30 m-3 mt-0 mb-0">
                  <div>
                    <strong>Invoice Attachment &nbsp; &nbsp;</strong>
                    {!readOnly && (
                      <Upload {...invoiceFileUploadProps}>
                        <CloudUploadOutlined />
                      </Upload>
                    )}
                  </div>
                  {InvoiceAttachment && !newInvoiceAttachment && (
                    <div>
                      <a
                        href={InvoiceAttachment?.filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {InvoiceAttachment?.fileName}
                      </a>
                    </div>
                  )}
                </div>

                <div className="w-30 m-3 mt-0 mb-0">
                  <strong>Attach Delivery Challan &nbsp; &nbsp;</strong>
                  {!readOnly && (
                    <Upload {...dcFileUploadProps} className="w-50">
                      <CloudUploadOutlined />
                    </Upload>
                  )}
                  {DCAttachment && !newDCAttachment && (
                    <div>
                      <a
                        href={DCAttachment?.filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {DCAttachment?.fileName}
                      </a>
                    </div>
                  )}
                </div>

                <div className="w-30 m-3 mt-0 mb-0">
                  <strong>Bank Details &nbsp; &nbsp;</strong>
                  <span
                    style={{
                      color: "#2263b3",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={() => setBankDetailsVisible(true)}
                  >
                    View
                  </span>
                </div>
              </div>
            </Panel>
          </AntCollapse>

          <AntModal
            title={undefined}
            visible={termsVisible}
            footer={null}
            onCancel={() => {
              setIsTermsAndConditionsChanged(false);
              setTermsVisible(false);
            }}
            width="60%"
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="d-flex">
              <h5 className="m-3">Terms and Conditions</h5>
              {!isTermsAndConditionsChanged && !readOnly && (
                <EditOutlined
                  onClick={() => setIsTermsAndConditionsChanged(true)}
                />
              )}
            </div>
            {isTermsAndConditionsChanged ? (
              <ReactQuill
                theme="snow"
                value={newTermsAndConditions}
                onChange={(data) => {
                  setNewTermsAndConditions(data);
                }}
                className="mb-3"
              />
            ) : termsAndConditions ? (
              <pre className="m-3">{termsAndConditions || ""}</pre>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Empty description="No Terms and Conditions Available for this Invoice" />
              </div>
            )}

            {isTermsAndConditionsChanged && (
              <ul className="actions clearfix d-flex">
                <li>
                  <Button
                    color="primary"
                    type="submit"
                    onClick={() => {
                      setTermsVisible(false);
                      setIsTermsAndConditionsChanged(true);
                      setShowSubmitBtn(true);
                    }}
                  >
                    Submit
                  </Button>
                </li>
                <li className="m-3 mt-0">
                  <Button
                    color="primary"
                    onClick={() => {
                      setIsTermsAndConditionsChanged(false);
                      setNewTermsAndConditions(termsAndConditions);
                    }}
                    className="btn-dim btn btn-secondary"
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            )}
          </AntModal>

          <AntModal
            title={undefined}
            visible={bankDetailsVisible}
            footer={null}
            onCancel={() => {
              setBankDetailsVisible(false);
            }}
            width="60%"
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <ShowSupplierBankInfo />
          </AntModal>

          {returnsDrawerVisible && (
            <Drawer
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <strong>Create Return</strong>
                  <div style={{ textAlign: "right" }}>
                    <Button
                      color="primary"
                      className="m-3 mt-0 mb-0"
                      disabled={createReturnLoading || !isReturnsFormValid}
                      onClick={async () => {
                        if (createReturnLoading) {
                          return;
                        }
                        try {
                          const formValues = await returnsForm.validateFields();
                          setCreateReturnLoading(true);

                          const confirmation = await Swal.fire({
                            title: "Create Return",
                            text: "Are you sure you want to create this return?",
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonText: "Yes, Create!",
                            cancelButtonText: "Cancel",
                          });

                          if (!confirmation.isConfirmed) {
                            setCreateReturnLoading(false);
                            return;
                          }

                          if (
                            formValues.approvalRequired === "yes" &&
                            returnsApprovals?.data?.length === 0
                          ) {
                            Swal.fire({
                              icon: "warning",
                              title: "Approvals Required",
                              text: "Please set approvals before creating the return.",
                            });
                            setCreateReturnLoading(false);
                            return;
                          }

                          const _formData = new FormData();
                          const filesData: any = [];

                          if (returnsAttachments?.length > 0) {
                            returnsAttachments.forEach((item: any) => {
                              _formData.append("files", item?.originFileObj);
                              filesData.push({
                                indexOfFileLocated: filesData.length,
                                module: "Return",
                                purpose: "attachment",
                              });
                            });
                          }

                          if (returnsSignature?.image) {
                            const signatureBlob = await fetch(
                              returnsSignature.image
                            ).then((r) => r.blob());
                            const signatureFile = new File(
                              [signatureBlob],
                              "signature.png",
                              { type: "image/png" }
                            );
                            _formData.append("files", signatureFile);
                            filesData.push({
                              indexOfFileLocated: filesData.length,
                              module: "Return",
                              purpose: "signature",
                            });
                          }

                          const returnPayload = {
                            returnData: {
                              ...(returnNumberingMode === 'manual' && formValues.returnNumber
                                ? { returnId: formValues.returnNumber }
                                : {}),
                              returnDate: formValues.returnDate
                                ? dayjs(formValues.returnDate).format(
                                  "YYYY-MM-DD"
                                )
                                : dayjs().format("YYYY-MM-DD"),
                              returnType: formValues.returnType,
                              invoiceNumber: Number(invoiceNumber),
                              reason: "",
                              approvalRequired:
                                formValues.approvalRequired === "yes",
                              isStrictApproval: Boolean(isStrictApproval),
                              description: "",
                              signatureLabel:
                                returnsSignature?.label ||
                                "Authorised Signatory",
                              lines: returnableLines.map((item: any) => ({
                                invoiceLineNo: item.lineNo,
                                returnQty: Number(item.returnsQuantity),
                                reason: item.returnReason || "",
                              })),
                            },
                            approvals: returnsApprovals.data,
                            filesData: filesData,
                          };

                          _formData.append(
                            "return",
                            JSON.stringify(returnPayload)
                          );

                          try {
                            const response = await invoiceApis.createReturn(
                              _formData
                            );
                            if (response.status) {
                              Swal.fire(
                                "Success!",
                                response.data?.message ||
                                response.message ||
                                "Return created successfully.",
                                "success"
                              );
                              closeReturnsDrawer();
                              fetchInvoiceData();
                            } else {
                              Swal.fire({
                                icon: "error",
                                title: "Error",
                                text:
                                  response.data?.message ||
                                  response.message ||
                                  "Failed to create return",
                              });
                            }
                          } catch (error: any) {
                            console.error("Error creating return:", error);
                            Swal.fire({
                              icon: "error",
                              title: "Error",
                              text:
                                error?.message ||
                                "Failed to create return. Please try again.",
                            });
                          } finally {
                            setCreateReturnLoading(false);
                          }
                        } catch (error) {
                          console.log("Validation failed:", error);
                          setCreateReturnLoading(false);
                        }
                      }}
                    >
                      {createReturnLoading ? (
                        <>
                          <Spinner size="sm" color="light" className="me-2" />
                          Creating...
                        </>
                      ) : (
                        "Create"
                      )}
                    </Button>
                  </div>
                </div>
              }
              placement="right"
              onClose={handleDrawerClose}
              open={returnsDrawerVisible}
              width="85vw"
              style={{
                position: "absolute",
                zIndex: 1100,
              }}
              styles={{
                body: { paddingBottom: 80, paddingTop: 20 },
                header: { padding: "8px 24px" },
              }}
            >
              {/* Return Information Fields */}
              <Form
                form={returnsForm}
                layout="vertical"
                autoComplete="off"
                onFieldsChange={() => {
                  const values = returnsForm.getFieldsValue();
                  setIsReturnsFormValid(computeReturnsFormValidity(values));
                }}
              >
                <div className="w-100">
                  <h6>Return Information</h6>
                  <div className="justify-start w-100 flex-wrap pt-3">
                    <Form.Item
                      name="invoiceNumber"
                      label={
                        <div className="data-label w-100 mb-1">
                          Invoice Number
                        </div>
                      }
                      className="w-30 p-2 paddingLeft10 pt-0 pb-0"
                      initialValue={OriginalData?.invoiceId}
                    >
                      <Input disabled className="w-100" />
                    </Form.Item>

                    <Form.Item
                      name="returnType"
                      label={
                        <div className="data-label w-100 mb-1">
                          Return Type <span className="mandatory">*</span>
                        </div>
                      }
                      className="w-30 p-2 paddingLeft10 pt-0 pb-0"
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: "Please select Return Type",
                        },
                      ]}
                    >
                      <Select
                        className="w-100"
                        placeholder="Select Return Type"
                        onChange={(value) => setReturnsType(value)}
                      >
                        <Option value="Material Return">Material Return</Option>
                        <Option value="Excess Material Return">
                          Excess Material Return
                        </Option>
                        <Option value="Defective Return">
                          Defective Return
                        </Option>
                        <Option value="Wrong Supply">Wrong Supply</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="returnNumber"
                      label={
                        <div className="data-label w-100 mb-1">
                          Return Number {returnNumberingMode === 'manual' && <span className="mandatory">*</span>}
                        </div>
                      }
                      className="w-30 p-2 paddingLeft10 pt-0 pb-0"
                      required={false}
                      rules={[
                        {
                          required: returnNumberingMode === 'manual',
                          message: "Please enter return number",
                        },
                      ]}
                    >
                      <Input
                        className="w-100"
                        disabled={returnNumberingMode === 'auto'}
                        placeholder={returnNumberingMode === 'auto' ? 'Auto-generated' : 'Enter return number'}
                        suffix={
                          <AntTooltip title="Click here to enable or disable auto-generation of return numbers.">
                            <SettingOutlined
                              style={{
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#1890ff',
                              }}
                              onClick={() => {
                                setTempReturnNumberingMode(returnNumberingMode);
                                setShowReturnNumberingModal(true);
                              }}
                            />
                          </AntTooltip>
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="returnDate"
                      label={
                        <div className="data-label w-100 mb-1">
                          Return Date <span className="mandatory">*</span>
                        </div>
                      }
                      className="w-30 p-2 paddingLeft10 pt-0 pb-0"
                      initialValue={dayjs()}
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: "Please select return date",
                        },
                      ]}
                    >
                      <DatePicker
                        className="w-100"
                        disabledDate={(current) => {
                          const invoiceDate = OriginalData?.invoiceDate
                            ? dayjs(OriginalData.invoiceDate)
                            : null;
                          const today = dayjs().endOf("day");

                          // Disable dates before invoice date or after today
                          return (
                            (invoiceDate &&
                              current &&
                              current < invoiceDate.startOf("day")) ||
                            (current && current > today)
                          );
                        }}
                      />
                    </Form.Item>

                    <div className="w-30 p-2 paddingLeft10 pt-0 pb-0">
                      <Form.Item
                        name="approvalRequired"
                        label={
                          <div className="data-label w-100 mb-1">
                            Approval Required{" "}
                            <span className="mandatory">*</span>
                          </div>
                        }
                        className="mb-0"
                        required={false}
                        rules={[
                          {
                            required: true,
                            message: "Please select approval required",
                          },
                        ]}
                      >
                        <Select
                          className="w-100"
                          placeholder="Select"
                          onChange={(value) => {
                            setReturnApprovalRequired(value);
                            if (value === "no") {
                              setReturnsApprovals({
                                open: false,
                                data: [],
                              });
                            }
                            const currentValues = {
                              ...returnsForm.getFieldsValue(),
                              approvalRequired: value,
                            };
                            setIsReturnsFormValid(
                              computeReturnsFormValidity(currentValues)
                            );
                          }}
                        >
                          <Option value="yes">Yes</Option>
                          <Option value="no">No</Option>
                        </Select>
                      </Form.Item>
                      {returnApprovalRequired === "yes" && (
                        <div style={{ marginTop: "5px" }}>
                          <Popover
                            content="Click here to set required approvals"
                            title="Approval Required"
                          >
                            <a
                              href="#"
                              className="success"
                              style={{ fontSize: "12px" }}
                              onClick={(e) => {
                                e.preventDefault();
                                setReturnsApprovals((pre: any) => {
                                  return {
                                    ...pre,
                                    open: true,
                                  };
                                });
                              }}
                            >
                              <EditOutlined /> Set Approvals &nbsp;
                              {returnsApprovals?.data?.length === 0 ? (
                                <span className="mandatory">
                                  Please set approvals
                                </span>
                              ) : (
                                <span className="text-success">
                                  ({returnsApprovals?.data?.length} approver(s)
                                  selected)
                                </span>
                              )}
                            </a>
                          </Popover>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>

              {/* Material Information Table */}
              <AntCollapse defaultActiveKey={["1", "2"]} className="mt-3">
                <Panel header={<h6>Material Information</h6>} key="1">
                  <div className="table-responsive poTable">
                    <div
                      style={{
                        width: screenSize - 320 + "px",
                        margin: "0px auto 15px auto",
                      }}
                    >
                      <Table
                        className="customTable"
                        columns={(() => {
                          const filteredCols = mergedColumns.filter(
                            (col: any) => {
                              return (
                                col.key !== "actions" &&
                                col.dataIndex !== "basicAmount" &&
                                col.dataIndex !== "grossTotal" &&
                                !col.children
                              );
                            }
                          );

                          // Find the indices of columns
                          const invoicedQtyIndex = filteredCols.findIndex(
                            (col: any) => col.dataIndex === "quantityInvoiced"
                          );
                          const dispatchedQtyIndex = filteredCols.findIndex(
                            (col: any) => col.dataIndex === "quantityDispatched"
                          );
                          const deliveredQtyIndex = filteredCols.findIndex(
                            (col: any) => col.dataIndex === "quantityDelivered"
                          );
                          const returnsQtyIndex = filteredCols.findIndex(
                            (col: any) => col.dataIndex === "returnsQuantity"
                          );

                          // Reorder: Invoiced Qty → Dispatched Qty → Received Qty → Return Qty
                          if (
                            invoicedQtyIndex !== -1 &&
                            dispatchedQtyIndex !== -1 &&
                            deliveredQtyIndex !== -1 &&
                            returnsQtyIndex !== -1
                          ) {
                            // Store columns to reorder
                            const dispatchedQtyCol = filteredCols[dispatchedQtyIndex];
                            const deliveredQtyCol = filteredCols[deliveredQtyIndex];
                            const returnsQtyCol = filteredCols[returnsQtyIndex];

                            // Remove columns in reverse order to avoid index shifting
                            const indicesToRemove = [
                              dispatchedQtyIndex,
                              deliveredQtyIndex,
                              returnsQtyIndex,
                            ].sort((a, b) => b - a);

                            indicesToRemove.forEach((idx) => {
                              filteredCols.splice(idx, 1);
                            });

                            // Find invoiced qty index again after removals
                            const newInvoicedIndex = filteredCols.findIndex(
                              (col: any) => col.dataIndex === "quantityInvoiced"
                            );

                            // Insert Dispatched Qty after Invoiced Qty
                            filteredCols.splice(
                              newInvoicedIndex + 1,
                              0,
                              dispatchedQtyCol
                            );

                            // Insert Received Qty after Dispatched Qty
                            filteredCols.splice(
                              newInvoicedIndex + 2,
                              0,
                              deliveredQtyCol
                            );

                            // Insert Return Qty after Received Qty
                            filteredCols.splice(newInvoicedIndex + 3, 0, {
                              ...returnsQtyCol,
                              hidden: false,
                              render: (text: any, record: any) =>
                                `${Number(record.returnsQuantity || 0)?.toFixed(
                                  2
                                )} ${record?.uom}`,
                            });

                            // Insert Reason column after Return Qty
                            filteredCols.splice(newInvoicedIndex + 4, 0, {
                              title: "Reason",
                              dataIndex: "returnReason",
                              key: "returnReason",
                              width: 200,
                              render: (
                                text: any,
                                record: any,
                                index: number
                              ) => (
                                <Input.TextArea
                                  rows={2}
                                  placeholder="Enter reason..."
                                  defaultValue={record.returnReason || ""}
                                  onChange={(e) => {
                                    const updatedLines = [...returnableLines];
                                    updatedLines[index] = {
                                      ...updatedLines[index],
                                      returnReason: e.target.value,
                                    };
                                    setReturnableLines(updatedLines);
                                  }}
                                />
                              ),
                            });
                          }

                          return filteredCols;
                        })()}
                        dataSource={returnableLines.map((item: any) => ({
                          ...item,
                          key: item?.lineNo,
                        }))}
                        scroll={{ x: 1000 }}
                        bordered
                        size="middle"
                        pagination={false}
                      />
                    </div>
                  </div>

                  {/* Total GST Section */}
                  <div className="d-flex">
                    <div className="w-50"></div>
                    <div className="w-50">
                      <div style={{ margin: "20px" }}>
                        <div className="w-100">
                          <div className="d-flex justify-content-between">
                            <div className="w-50 justify-end">
                              <strong>Taxable Amount</strong>
                            </div>
                            <span className="r_align">
                              {formatCurrency(
                                returnableLines.reduce((a: any, b: any) => {
                                  const amount =
                                    Number(b.unitPrice) *
                                    Number(b.returnsQuantity || 0);
                                  return a + amount;
                                }, 0),
                                currency?.currencyCode
                              )}
                            </span>
                          </div>
                          {returnableLines[0]?.cgstPercentage !== undefined &&
                            returnableLines[0]?.sgstPercentage !== undefined &&
                            returnableLines[0]?.cgstPercentage !== null &&
                            returnableLines[0]?.sgstPercentage !== null ? (
                            <>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>CGST</strong>
                                </div>
                                <span className="r_align">
                                  {formatCurrency(
                                    returnableLines.reduce((a: any, b: any) => {
                                      const amount =
                                        Number(b.unitPrice) *
                                        Number(b.returnsQuantity || 0);
                                      return (
                                        a +
                                        (amount *
                                          Number(b.cgstPercentage || 0)) /
                                        100
                                      );
                                    }, 0),
                                    currency?.currencyCode
                                  )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>SGST</strong>
                                </div>
                                <span className="r_align">
                                  {formatCurrency(
                                    returnableLines.reduce((a: any, b: any) => {
                                      const amount =
                                        Number(b.unitPrice) *
                                        Number(b.returnsQuantity || 0);
                                      return (
                                        a +
                                        (amount *
                                          Number(b.sgstPercentage || 0)) /
                                        100
                                      );
                                    }, 0),
                                    currency?.currencyCode
                                  )}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex justify-content-between">
                              <div className="w-50 justify-end">
                                <strong>IGST</strong>
                              </div>
                              <span className="r_align">
                                {formatCurrency(
                                  returnableLines.reduce((a: any, b: any) => {
                                    const amount =
                                      Number(b.unitPrice) *
                                      Number(b.returnsQuantity || 0);
                                    return (
                                      a +
                                      (amount * Number(b.igstPercentage || 0)) /
                                      100
                                    );
                                  }, 0),
                                  currency?.currencyCode
                                )}
                              </span>
                            </div>
                          )}
                          <div
                            className="d-flex justify-content-between"
                            style={{ fontSize: "18px", marginTop: "10px" }}
                          >
                            <div className="w-50 justify-end">
                              <strong>Total</strong>
                            </div>
                            <strong className="r_align">
                              {formatCurrency(
                                returnableLines.reduce((a: any, b: any) => {
                                  const amount =
                                    Number(b.unitPrice) *
                                    Number(b.returnsQuantity || 0);
                                  const cgst =
                                    (amount * Number(b.cgstPercentage || 0)) /
                                    100;
                                  const sgst =
                                    (amount * Number(b.sgstPercentage || 0)) /
                                    100;
                                  const igst =
                                    (amount * Number(b.igstPercentage || 0)) /
                                    100;
                                  return a + amount + cgst + sgst + igst;
                                }, 0),
                                currency?.currencyCode
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Panel>

                {/* Attachments Section */}
                <Panel header={<h6>Attachments (If Any)</h6>} key="2">
                  <Dragger
                    beforeUpload={beforeUpload}
                    onChange={handleReturnsAttachmentChange}
                    fileList={returnsAttachments}
                    maxCount={3}
                    multiple={true}
                    accept=".pdf,.jpg,.jpeg,.png"
                  >
                    <Button className="btn-dim btn">
                      <UploadOutlined />
                      <span> Click to Upload</span>
                    </Button>
                  </Dragger>
                </Panel>
              </AntCollapse>

              {/* Signature Section */}
              <Row className="m-4 justify-content-end">
                {returnsSignature?.isActive ? (
                  <div className="signatureBtnWrapper">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Signature</strong>
                      <a
                        style={{
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setReturnsSignature({
                            isActive: false,
                            image: null,
                            label: "Authorised Signatory",
                          });
                        }}
                      >
                        <Icon name="cross" className="pointer" />
                      </a>
                    </div>
                    <div
                      style={{
                        minHeight: returnsSignature?.image ? "0px" : "120px",
                        display: returnsSignature?.image ? "block" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {!returnsSignature?.image && (
                        <Button
                          color="btn-dim btn"
                          onClick={() => {
                            setShowReturnsCropModel(true);
                          }}
                        >
                          <Icon name="upload-cloud" className="pointer" />
                          <span>Upload</span>
                        </Button>
                      )}
                      {returnsSignature?.image && (
                        <>
                          <div className="signatueImgWrapper border">
                            <img
                              src={returnsSignature?.image}
                              alt="Signed"
                              style={{ width: "160px" }}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              color="btn-dim btn"
                              onClick={() => {
                                setReturnsSignature((pre: any) => {
                                  return { ...pre, image: null };
                                });
                              }}
                            >
                              <span>Reset</span>
                              <Icon name="redo" className="pointer" />
                            </Button>
                          </div>
                          <div>
                            <div className="form-group">
                              <label
                                className="form-label"
                                htmlFor="signatureLabel"
                              >
                                Signature Label
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="signatureLabel"
                                onChange={(e) => {
                                  setReturnsSignature((pre: any) => {
                                    return {
                                      ...pre,
                                      label: e.target.value,
                                    };
                                  });
                                }}
                                defaultValue={
                                  returnsSignature?.label ||
                                  "Authorised Signatory"
                                }
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {!returnsSignature?.image && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexDirection: "column",
                        }}
                      >
                        <Button
                          color="btn-dim btn"
                          onClick={() => setShowReturnsSignatureModel(true)}
                        >
                          <Icon name="pen" />
                          <span>Use Signature Pad</span>
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1px solid #e5ecf7",
                      backgroundColor: "#f7faff",
                      padding: "10px",
                      marginLeft: "10px",
                      width: "22%",
                      borderRadius: "5px",
                      textAlign: "center",
                    }}
                  >
                    <Button
                      color="btn-dim btn"
                      onClick={() => {
                        setReturnsSignature((pre: any) => {
                          return { ...pre, isActive: true };
                        });
                      }}
                    >
                      <Icon name="edit" className="pointer" />
                      <span>Add Signature</span>
                    </Button>
                  </div>
                )}
              </Row>

              {/* Signature Modals */}
              {showReturnsCropModel && (
                <CropperComponent
                  modal={showReturnsCropModel}
                  toggle={() => setShowReturnsCropModel(!showReturnsCropModel)}
                  onSave={(data: string) => {
                    setReturnsSignature((pre: any) => {
                      return { ...pre, image: data };
                    });
                    setShowReturnsCropModel(false);
                  }}
                />
              )}
              {showReturnsSignatureModel && (
                <SignatureModal
                  modal={showReturnsSignatureModel}
                  toggle={() =>
                    setShowReturnsSignatureModel(!showReturnsSignatureModel)
                  }
                  onSave={(data: string) => {
                    setReturnsSignature((pre: any) => {
                      return { ...pre, image: data };
                    });
                    setShowReturnsSignatureModel(false);
                  }}
                />
              )}
            </Drawer>
          )}

          {/* Return Approval Drawer */}
          {returnsApprovals.open && (
            <ReturnApprovalDrawer
              open={returnsApprovals.open}
              siteId={OriginalData?.siteId}
              onClose={() => {
                setReturnsApprovals((pre: any) => {
                  return {
                    ...pre,
                    open: false,
                  };
                });
              }}
              onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
                setReturnsApprovals({
                  open: false,
                  data: data,
                });
                setIsStrictApproval(isStrictApprovalFromDrawer);
              }}
              initialStrictApproval={isStrictApproval}
              setApprovalSubmissionStatus={(status: boolean) => { }}
              approvalData={returnsApprovals.data}
            />
          )}

          {/* Return Number Configuration Modal */}
          <AntModal
            title="Configure Return Number Preference"
            open={showReturnNumberingModal}
            onCancel={() => setShowReturnNumberingModal(false)}
            zIndex={1200}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
                <Button
                  key="save"
                  color="primary"
                  onClick={() => {
                    const currentSequence = getCurrentReturnSequence();
                    if (tempReturnNumberingMode === "auto" && !currentSequence) {
                      message.error("Auto-generation is not configured for this site.");
                      return;
                    }

                    setReturnNumberingMode(tempReturnNumberingMode);

                    if (tempReturnNumberingMode === "auto" && currentSequence) {
                      const { prefix } = currentSequence;
                      returnsForm.setFieldsValue({ returnNumber: prefix });
                    } else if (tempReturnNumberingMode === "manual") {
                      returnsForm.setFieldsValue({ returnNumber: "" });
                    }
                    setShowReturnNumberingModal(false);
                    // Recompute form validity
                    const values = returnsForm.getFieldsValue();
                    setIsReturnsFormValid(computeReturnsFormValidity(values));
                  }}
                >
                  Save
                </Button>
                <Button
                  key="cancel"
                  color="primary"
                  onClick={() => setShowReturnNumberingModal(false)}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            <div style={{ marginBottom: 20, lineHeight: "22px" }}>
              Your return numbers are currently set to auto-generate to save time.
              <br />
              Are you sure you want to change this setting?
            </div>

            {!OriginalData?.siteId && (
              <div style={{ color: "#8c8c8c", marginBottom: 16 }}>
                <InfoCircleOutlined /> Sequence configuration is not set for this site. Please configure the sequence.
              </div>
            )}

            {OriginalData?.siteId && !getCurrentReturnSequence() && (
              <div style={{ color: "#faad14", marginBottom: 16 }}>
                <InfoCircleOutlined />{" "}
                {activeReturnSequences.length === 0
                  ? "No sequences configured for Returns. Please set up a sequence first."
                  : "No sequence configured for the selected site. Please set a sequence for this site."}
              </div>
            )}

            <Radio.Group
              onChange={(e) => setTempReturnNumberingMode(e.target.value)}
              value={tempReturnNumberingMode}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value="auto" disabled={!getCurrentReturnSequence()}>
                  <span style={{ fontWeight: 500 }}>
                    Continue auto-generating return numbers
                  </span>
                </Radio>
                <Radio value="manual">
                  <span style={{ fontWeight: 500 }}>
                    Enter return numbers manually
                  </span>
                </Radio>
              </Space>
            </Radio.Group>
          </AntModal>
        </div>
      )}
    </div>
  );
}

const RejectInvoice = ({
  isModalVisible,
  setIsModalVisible,
  rejectInvoice,
}: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        rejectInvoice(reason); // Call your reject function here
        form.resetFields();
        setIsModalVisible(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <AntModal
      title="Reject Purchase Order"
      visible={isModalVisible}
      footer={null} // Remove the default footer buttons
      onCancel={handleCancel}
    >
      <Form form={form} layout="vertical" name="rejectForm">
        <Form.Item
          name="reason"
          label="Reason for rejection"
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." />
        </Form.Item>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit" onClick={handleOk}>
                Submit
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={handleCancel}
                className="btn-dim btn btn-secondary"
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      </Form>
    </AntModal>
  );
};

export default InvoiceComponent;
